import type { Capability } from './identity.constant';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  WHERE A SHORTCUT TILE MAY POINT. [P6b.1]
 *
 *  [P6a] deleted `pages/Home`, and with it three capability-gated cards that took an operator
 *  straight to the inbox, the org tree and the officer list. The dashboard replaced the
 *  NUMBERS it showed and not the DOORS, so a nodal who used to land one click from "assign
 *  names" now navigates the sidebar for it. A shortcut tile puts those doors back — and makes
 *  WHICH doors a configuration question, which is the whole thesis of the registry.
 *
 *  ⚠️ A DESTINATION, NEVER AN HREF — `DrillTarget`'s rule, and for its reason. `pwa` holds
 *  five routes and parses no query string, so a path is not portable across the two clients.
 *  Each client owns ONE total mapper; a member added here is a compile error in every client
 *  rather than a link that silently goes nowhere.
 *
 *  ⚠️ AND A CHOSEN DESTINATION, NEVER A TYPED ONE. "Link it to any page" is satisfied by a
 *  list of every page the app HAS — and a typed path could not be. Three things fall out of
 *  the list that free text cannot give: a shortcut cannot point outside the product, which on
 *  a police dashboard is an open-redirect-shaped affordance an operator should not be able to
 *  create; a typo cannot become a dead tile; and, most importantly, each destination CARRIES
 *  THE CAPABILITY ITS ROUTE IS GATED ON, so a shortcut a role could only ever bounce off
 *  `/forbidden` is refused when it is saved and dropped if the role's access later narrows.
 *
 *  ⚠️ NO ENGLISH HERE, `dashboardTile.constant.ts`'s rule. The words are
 *  `DASHBOARD_SHORTCUT_LABEL` in `web/src/lib/labels.ts`, exhaustive, so a destination added
 *  here without words is a compile error rather than a blank card.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export const DASHBOARD_SHORTCUT_TARGETS = [
  'INBOX',
  'ARRANGEMENTS',
  'ARRANGEMENT_NEW',
  'STANDING_ORDERS',
  'ORG_TREE',
  'OFFICERS',
  'PLACES',
  'POLICIES',
  'POST_TYPES',
  'VEHICLE_TYPES',
] as const;

export type DashboardShortcutTarget = (typeof DASHBOARD_SHORTCUT_TARGETS)[number];

/**
 * ⚠️ THE CAPABILITY IS THE ROUTE'S OWN, COPIED FROM `web/src/routes/index.tsx`.
 *
 * `/arrangements/new` takes `arrangement.write` rather than `arrangement.read` — that file's
 * stated convention, because "new" has no read-only meaning and a `/forbidden` bounce beats a
 * form that cannot be saved. A shortcut inherits it, so a NODAL_READ layout cannot be given a
 * tile whose only outcome is a bounce.
 *
 * ⚠️ `/dashboard-layouts` IS DELIBERATELY ABSENT. It is SUPER_ADMIN-only configuration, and a
 * shortcut to it would appear on exactly one layout and be dropped from every other — a slot
 * spent on a door only the person configuring it can already open.
 */
export const DASHBOARD_SHORTCUT_CAPABILITY = {
  INBOX: 'arrangement.read',
  ARRANGEMENTS: 'arrangement.read',
  ARRANGEMENT_NEW: 'arrangement.write',
  STANDING_ORDERS: 'standingOrder.read',
  ORG_TREE: 'org.read',
  OFFICERS: 'officer.read',
  PLACES: 'place.read',
  POLICIES: 'policy.read',
  POST_TYPES: 'postType.read',
  VEHICLE_TYPES: 'vehicleType.read',
} as const satisfies Record<DashboardShortcutTarget, Capability>;

/** Narrowing for anything reading a target off the wire or out of a stored row. */
export const isDashboardShortcutTarget = (value: string): value is DashboardShortcutTarget =>
  (DASHBOARD_SHORTCUT_TARGETS as readonly string[]).includes(value);
